import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import StarField from '../home/components/StarField';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import Community from '../home/components/Community';
import FAQ from '../home/components/FAQ';
import AuthModal from '../home/components/AuthModal';
import { courses } from '../home/components/Courses';
import { useLang } from '../../i18n/LanguageContext';

interface WallReview {
  id: string;
  name: string;
  course: string;
  courseEn?: string;
  rating: number;
  content: string;
  contentEn?: string;
  date: string;
  isUserSubmitted?: boolean;
}

const coursePaths = [
  {
    icon: 'ri-eye-line', keyEn: 'Perception', keyZh: '感知',
    tagEn: 'Path 01', tagZh: '路径 01',
    titleEn: 'Perception Path', titleZh: '感知路径',
    descEn: 'Awaken your innate sensitivity. Learn to read energy fields, tune into subtle signals from the universe, and develop your intuitive sight through structured, repeatable practices.',
    descZh: '唤醒与生俱来的感知能力。学习读取能量场、捕捉宇宙的细微信号，通过结构化、可重复的练习发展直觉洞察力。',
    courses: ['Tarot Level I', 'Intuitive Sensing', 'Daily Card Practice'],
    coursesZh: ['塔罗一阶', '直觉感知', '每日牌卡练习'],
    color: 'from-[#9b59b6] to-[#6c3483]', border: 'border-[#9b59b6]/40', glow: 'hover:shadow-[#9b59b6]/30',
  },
  {
    icon: 'ri-book-open-line', keyEn: 'Interpretation', keyZh: '解读',
    tagEn: 'Path 02', tagZh: '路径 02',
    titleEn: 'Interpretation Path', titleZh: '解读路径',
    descEn: 'Master the language of symbols, archetypes, and astrological charts. Turn scattered signs into coherent narratives — for yourself and for others.',
    descZh: '掌握符号、原型与星盘的语言。将分散的信号转化为连贯叙事，服务于自己与他人。',
    courses: ['Tarot Level II & III', 'Birth Chart Reading', 'Symbol Decoding'],
    coursesZh: ['塔罗一阶二阶', '星盘解读', '符号解码'],
    color: 'from-[#d4af37] to-[#b8860b]', border: 'border-[#d4af37]/40', glow: 'hover:shadow-[#d4af37]/30',
  },
  {
    icon: 'ri-seedling-line', keyEn: 'Practice', keyZh: '实践',
    tagEn: 'Path 03', tagZh: '路径 03',
    titleEn: 'Practice Path', titleZh: '实践路径',
    descEn: 'Hands-on, repeatable, and grounded. From herbal formulas and ritual design to moon-cycle work — build real skills you can use in everyday life.',
    descZh: '实操导向、可重复落地。从草药配方、仪式设计到月相周期工作，积累在日常生活中真正可用的能力。',
    courses: ['Herbal Magic I & II', 'Ritual Design', 'Moon Phase Magic'],
    coursesZh: ['草药魔法一阶二阶', '仪式设计', '月相魔法'],
    color: 'from-[#27ae60] to-[#1a5c3a]', border: 'border-[#27ae60]/40', glow: 'hover:shadow-[#27ae60]/30',
  },
  {
    icon: 'ri-fire-line', keyEn: 'Energy', keyZh: '能量',
    tagEn: 'Path 04', tagZh: '路径 04',
    titleEn: 'Energy Path', titleZh: '能量路径',
    descEn: 'Work directly with life force energy. Learn touch healing, energy reset, and body-field communication — practical methods with measurable results.',
    descZh: '直接与生命力能量共事。学习触感疗愈、能量重置与身体场沟通——有可衡量效果的实用方法。',
    courses: ['Energy Reset', 'Touch Healing', 'Aura Reading'],
    coursesZh: ['能量重置', '触感疗愈', '气场解读'],
    color: 'from-[#e74c3c] to-[#922b21]', border: 'border-[#e74c3c]/40', glow: 'hover:shadow-[#e74c3c]/30',
  },
];

const mockWallReviews: WallReview[] = [
  {
    id: 'mock-1',
    name: 'Sophia L',
    course: '专业塔罗课程一阶',
    courseEn: 'Professional Tarot Course · Level I',
    rating: 5,
    content: '之前我抽牌很依赖书本含义，现在更多是在理解牌之间的关系和结构。解读会更顺，也更贴近问题本身，而不是在背关键词。谢谢Aina老师！',
    contentEn: 'I used to rely heavily on textbook meanings when reading cards. Now I focus more on understanding the relationships and structure between cards. My readings flow more naturally and stay closer to the actual question — not just reciting keywords. Thank you Teacher Aina!',
    date: '2023-12-05',
  },
  {
    id: 'mock-2',
    name: 'Lin Xiaoyu',
    course: '灵视力与通灵沟通课程',
    courseEn: 'Intuitive Sight & Communication',
    rating: 5,
    content: '一开始我其实是半信半疑来的，但上完课之后最大的变化是终于知道自己在感知什么。以前很多直觉都被我自己否掉了，现在反而变得更稳定、更有边界感了。',
    contentEn: 'I honestly came with some skepticism, but the biggest change after finishing was finally knowing what I\'m actually perceiving. I used to dismiss many of my intuitions — now they come through more steadily, with much clearer boundaries.',
    date: '2024-11-03',
  },
  {
    id: 'mock-3',
    name: 'Mara T',
    course: '实用草药魔法课程一阶',
    courseEn: 'Practical Herbal Magic Course · Level I',
    rating: 5,
    content: '一开始以为就是学配方，上完课之后才发现重点在于怎么去感受每一株植物。现在我在调配的时候，会很自然地去判断它们之间是不是协调了。',
    contentEn: 'I thought it was just about learning formulas, but after finishing I realized the point was about sensing each plant. Now when I blend, I naturally assess whether they\'re in harmony with each other.',
    date: '2025-09-28',
  },
  {
    id: 'mock-4',
    name: 'Chen Siyuan',
    course: '专业占星课程一阶',
    courseEn: 'Professional Astrology Course · Level I',
    rating: 5,
    content: '以前觉得占星很准但说不清，现在是我能解释为什么会这样。那种从"模糊感"到"可表达"的过程，还挺有成就感的。',
    contentEn: 'Astrology always felt accurate to me, but I couldn\'t explain why. Now I can articulate the reasoning. The journey from "a vague feeling" to "something I can actually express" is genuinely satisfying.',
    date: '2025-12-01',
  },
  {
    id: 'mock-5',
    name: 'Elena W',
    course: '专业塔罗课程一阶',
    courseEn: 'Professional Tarot Course · Level I',
    rating: 5,
    content: '我之前学过不少塔罗相关的知识，但在这里是第一次把这些东西的结构梳理出来，不是零散知识，而是可以慢慢搭建起来的一套系统。',
    contentEn: 'I\'d studied a lot of tarot-related material before, but here was the first time I actually organized the underlying structure — not scattered knowledge, but a coherent system I could build upon step by step.',
    date: '2025-01-15',
  },
  {
    id: 'mock-6',
    name: 'Fang Mubai',
    course: '实用草药魔法课程一阶',
    courseEn: 'Practical Herbal Magic Course · Level I',
    rating: 5,
    content: 'Aina老师的课程让我深切意识到植物其实是有智慧的，万物皆有灵，这句话已经深深刻入我心。你在什么状态下去与植物合作，结果真的会不一样，除了震惊更多的还是感激！',
    contentEn: 'Aina\'s course made me truly realize that plants carry their own wisdom. "Everything has spirit" — those words are now carved deep into my heart. The state you\'re in when working with plants genuinely changes the outcome. Beyond amazement, what I feel most is gratitude!',
    date: '2026-03-22',
  },
  {
    id: 'mock-7',
    name: 'Rika S',
    course: '月相魔法实践课程',
    courseEn: 'Lunar Magic Course · Ritual Practice',
    rating: 5,
    content: '以前我只是知道新月满月可以许愿，但这里会讲清楚每个阶段到底适合做什么。现在我会更有节奏地安排自己的计划，而不是随便跟风，感恩！',
    contentEn: 'I used to only know that new and full moons were for making wishes. Here I learned what each phase is genuinely suited for. I now plan my schedule with much more rhythm instead of just following trends. So grateful!',
    date: '2025-08-20',
  },
  {
    id: 'mock-8',
    name: 'James H',
    course: '能量重置与触感疗愈课程',
    courseEn: 'Energy Reset & Touch Healing Course',
    rating: 5,
    content: '这个课程让我对身体记忆有了很深的感受。有些卡点不是靠想能解决的，但通过引导可以慢慢松开。现在我很有信心作为一名能量疗愈师！',
    contentEn: 'This course gave me a deep understanding of body memory. Some blocks can\'t be resolved by thinking alone, but through guided practice they gradually release. I now feel genuinely confident as an energy healer!',
    date: '2025-07-14',
  },
  {
    id: 'mock-9',
    name: 'Wu Mingzhi',
    course: '水晶&脉轮疗愈课程',
    courseEn: 'Crystal & Chakra Healing Course',
    rating: 5,
    content: '水晶脉轮疗愈课程改变了我对能量工作的理解，它不再是一门玄学，而是一套可以实际操作和验证的系统，甚至我可以把它应用在生活中的各个方面，非常推荐。',
    contentEn: 'This course transformed my understanding of energy work — it\'s no longer esoteric mysticism, but a practical, verifiable system I can apply across so many areas of life. Highly, highly recommended.',
    date: '2024-01-30',
  },
];

const LS_KEY = 'mystic_feedback_wall';

function loadLocalReviews(): WallReview[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalReview(review: WallReview) {
  const existing = loadLocalReviews();
  const updated = [review, ...existing];
  localStorage.setItem(LS_KEY, JSON.stringify(updated));
}

export default function AcademyPage() {
  const { t, lang } = useLang();
  const [currentUser, setCurrentUser] = useState<{ name: string; email: string } | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', course: '', rating: 0, hoverRating: 0, content: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [wallReviews, setWallReviews] = useState<WallReview[]>([]);
  const [showAll, setShowAll] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const refreshWall = () => {
    const local = loadLocalReviews();
    setWallReviews([...local, ...mockWallReviews]);
  };

  useEffect(() => {
    refreshWall();
    const stored = localStorage.getItem('mystic_current_user');
    if (stored) {
      try { setCurrentUser(JSON.parse(stored)); } catch { /* ignore */ }
    }
    const onStorage = () => {
      const s = localStorage.getItem('mystic_current_user');
      setCurrentUser(s ? JSON.parse(s) : null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleAuthSuccess = (user: { name: string; email: string }) => {
    setCurrentUser(user);
    setShowAuthModal(false);
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackForm.course || !feedbackForm.rating || !feedbackForm.content.trim() || !feedbackForm.name.trim()) return;
    setSubmitStatus('loading');
    try {
      const params = new URLSearchParams();
      params.append('name', feedbackForm.name.trim() || currentUser?.name || '');
      params.append('email', currentUser?.email || '');
      params.append('course', feedbackForm.course);
      params.append('rating', String(feedbackForm.rating));
      params.append('content', feedbackForm.content);
      const res = await fetch('https://readdy.ai/api/form/d74jqcb41deorl02es7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        const newReview: WallReview = {
          id: `user-${Date.now()}`,
          name: feedbackForm.name.trim() || currentUser?.name || 'Anonymous',
          course: feedbackForm.course,
          rating: feedbackForm.rating,
          content: feedbackForm.content,
          date: new Date().toISOString().slice(0, 10),
          isUserSubmitted: true,
        };
        saveLocalReview(newReview);
        refreshWall();
        setSubmitStatus('success');
        setFeedbackForm({ name: '', course: '', rating: 0, hoverRating: 0, content: '' });
        setShowAll(true);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  const avgRating = wallReviews.length > 0
    ? (wallReviews.reduce((sum, r) => sum + r.rating, 0) / wallReviews.length).toFixed(1)
    : '5.0';

  const displayedReviews = showAll ? wallReviews : wallReviews.slice(0, 9);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e] overflow-hidden">
      <StarField />
      <Navbar />

      {/* Hero */}
      <section className="relative z-10 pt-44 pb-20 px-4 sm:px-8 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img
            src="https://public.readdy.ai/ai/img_res/3bcb7f01ca16dd9222e7c8e64daac239.jpg"
            alt="bg"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a0b2e]/60 via-transparent to-[#1a0b2e]"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#d4af37] via-[#c8b6ff] to-[#d4af37] bg-clip-text text-transparent leading-tight">
            {t('About Academy', '学院介绍')}
          </h1>
          <p className="text-lg sm:text-xl text-[#c8b6ff] leading-relaxed mb-10 max-w-2xl mx-auto">
            {t('SSD Mystic Academy is a globally recognized institution for professional mystical arts education — where ancient wisdom meets modern learning.', 'SSD国际神秘学院｜一套可被学习与验证的能力体系')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/courses" className="px-8 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] font-bold rounded-full hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all duration-300 cursor-pointer whitespace-nowrap">
              {t('Explore Courses', '探索课程')}
            </Link>
            <Link to="/membership" className="px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap">
              {t('View Membership', '查看会员体系')}
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="relative z-10 py-16 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">{t('Our Mission', '我们的使命')}</h2>
          <p className="text-[#c8b6ff] leading-relaxed mb-4 text-lg">
            {t('We believe that the mystical arts are a profound path of self-discovery, healing, and empowerment. Our mission is to provide rigorous, accessible, and internationally accredited education that honors the depth of these traditions while making them relevant to modern life.', '我们相信神秘学是一条深刻的自我探索、疗愈与赋能之路。我们的使命是提供严谨、可及且国际认证的教育，尊重这些传统的深度，同时使其与现代生活紧密相连。')}
          </p>
          <div className="space-y-3 mb-10">
            <p className="text-[#c8b6ff] leading-relaxed text-lg">
              {t('We are dedicated to reintegrating scattered knowledge systems in a clearer, more actionable way — helping individuals complete the transformation from perception to application.', '我们致力于将分散的知识体系重新整合，用更清晰、可执行的方式，让个体完成从 感知 到 应用 的转化。这里不强调天赋崇拜，而更关注如何让能力稳定出现，并被现实承载。')}
            </p>
            <p className="text-[#c8b6ff] leading-relaxed text-lg">
              {t('We do not glorify innate talent here — we focus on how abilities can appear consistently, and be carried into reality.', '自2015年创立以来，我们已引导10余个国家超过1000名学员，通过由大师修行者设计的转化性课程完成蜕变之旅。')}
            </p>
          </div>
          <div className="w-full rounded-3xl overflow-hidden border border-[#d4af37]/20 bg-[#1a0b2e]/40">
            <img
              src="https://storage.readdy-site.link/project_files/f2f851a6-6e4f-4fec-9da8-8bbdf3dce65c/526367c8-60ac-4ca3-98bc-674a1cccf557_ChatGPT-Image-2026329-21_02_59.png?v=3ae8c407fcd729438af4c895f1aa9ab8"
              alt="Academy Mission"
              className="w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('The Mind Behind the Academy', '学院创始人简介')}</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-8 items-start">
            <div className="relative flex-shrink-0">
              <div className="relative w-32">
                <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-[#d4af37]/20 via-[#9b59b6]/10 to-transparent blur-lg"></div>
                <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/30">
                  <img src="https://static.readdy.ai/image/f4ea41724fdff553620ff2345610d616/78db927fd8b30b5402e0018fd41f41eb.png" alt="Endina Jane Aina" className="w-full h-40 object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a0b2e]/50 to-transparent"></div>
                </div>
                <div className="absolute -bottom-3 -right-3 bg-gradient-to-br from-[#d4af37] to-[#f4d03f] rounded-xl px-3 py-1.5 text-center">
                  <div className="text-[#1a0b2e] font-bold text-sm leading-none">10</div>
                  <div className="text-[#1a0b2e] text-xs font-semibold mt-0.5">{t('Years Practice', '修行经验')}</div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-1">Endina Jane</h3>
              <p className="text-[#d4af37] text-lg font-medium mb-6">{t('Founder & Lead Instructor · Aina', '创始人 & 首席导师 · Aina')}</p>
              <p className="text-[#c8b6ff] leading-relaxed text-base mb-5">
                {t('Endina Jane is the founder of SSD Mystic Academy — known to her students as Aina. With extensive hands-on experience across multiple mystical traditions, spanning herbology, astrology, energy work, and ritual practice, she is dedicated to translating intuitive knowledge into learnable, reproducible methodologies. Over the years, she has guided countless students through transformative training — emphasizing awareness over belief, practice and method over mysticism — continually exploring what truly works across mystical systems, and the fundamental reasons why.', 'Endina Jane 是 SSD 神秘学院的创始人，亦以 Aina 为名开展分享与教学。在多个神秘学体系中拥有长期实践经验，横跨草药学、占星、能量工作与仪式，致力于将直觉性的认知转化为可被学习与复现的方法体系。多年来，引导了众多学员完成培训，强调以觉察代替信念，以实践/方法代替玄虚，不断在各个神秘学体系中探索什么是真正有效的，而有效的根本原因在于什么。')}
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { en: 'Herbal Magic Instructor', zh: '魔药学导师' },
                  { en: 'Energy Healer', zh: '能量疗愈师' },
                  { en: 'Professional Tarotist', zh: '职业塔罗师' },
                  { en: 'Professional Astrologer', zh: '职业占星师' },
                  { en: 'Sound Healer', zh: '音疗师' },
                ].map((tag) => (
                  <span key={tag.en} className="px-4 py-1.5 rounded-full border border-[#d4af37]/40 text-[#d4af37] text-sm font-medium bg-[#d4af37]/5">
                    {t(tag.en, tag.zh)}
                  </span>
                ))}
              </div>
              <div className="border-l-2 border-[#d4af37]/50 pl-5">
                <p className="text-white italic text-base leading-relaxed">
                  {t('"I don\'t teach you to believe. I teach you to observe, practice, and verify — then make up your own mind."', '不教你去相信，而带你去观察、练习、验证，然后你会自己得出结论。')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four Course Paths */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#d4af37] text-sm font-semibold tracking-widest uppercase mb-3">{t('Curriculum Architecture', '课程体系')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('Four Paths. One Complete System.', '四大路径，一套完整体系')}</h2>
            <p className="text-[#c8b6ff] text-lg max-w-2xl mx-auto">
              {t('Each path is standalone, and they\'re designed to build on each other. Start anywhere — but know where each one leads.', '每条路径独立成体，同时彼此相互支撑。从任意一条开始——但要知道每条路通向哪里。')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coursePaths.map((path) => (
              <div key={path.keyEn} className={`relative group bg-[#2d1b4e]/50 backdrop-blur-md rounded-2xl p-6 border ${path.border} hover:shadow-xl ${path.glow} transition-all duration-300 cursor-default flex flex-col`}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${path.color} mb-5`}>
                  <i className={`${path.icon} text-white text-xl`}></i>
                </div>
                <div className="text-xs font-semibold tracking-widest text-white/40 uppercase mb-1">{t(path.tagEn, path.tagZh)}</div>
                <h3 className="text-xl font-bold text-white mb-3">{t(path.titleEn, path.titleZh)}</h3>
                <p className="text-[#c8b6ff] text-sm leading-relaxed mb-5 flex-1">{t(path.descEn, path.descZh)}</p>
                <div className="space-y-1.5">
                  {path.courses.map((c, i) => (
                    <div key={c} className="flex items-center gap-2 text-sm text-white/70">
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${path.color} flex-shrink-0`}></span>
                      {t(c, path.coursesZh[i])}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/courses" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] font-bold rounded-full hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all duration-300 cursor-pointer whitespace-nowrap">
              {t('View All Courses', '查看全部课程')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ Feedback Wall + Form ═══ */}
      <section className="relative z-10 py-20 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Section header */}
          <div className="text-center mb-6">
            <p className="text-[#d4af37] text-sm font-semibold tracking-widest uppercase mb-3">{t('Student Voices', '学员声音')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('Feedback Wall', '学员反馈墙')}</h2>
            <p className="text-[#c8b6ff] text-lg mb-6">{t('Real words from students across all paths — updated in real time.', '来自各路径学员的真实反馈，实时更新。')}</p>
          </div>



          {/* Wall grid */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5">
            {displayedReviews.map((review) => (
              <div
                key={review.id}
                className={`break-inside-avoid bg-[#2d1b4e]/50 backdrop-blur-md rounded-2xl p-6 border transition-all duration-300 flex flex-col gap-3
                  ${review.isUserSubmitted
                    ? 'border-[#d4af37]/60 ring-1 ring-[#d4af37]/20'
                    : 'border-[#d4af37]/15 hover:border-[#d4af37]/35'
                  }`}
              >
                {/* Top: stars + new badge */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className={`text-sm ${s <= review.rating ? 'ri-star-fill text-[#d4af37]' : 'ri-star-line text-white/20'}`}></i>
                    ))}
                  </div>
                  {review.isUserSubmitted && (
                    <span className="px-2 py-0.5 bg-[#d4af37]/20 border border-[#d4af37]/40 text-[#d4af37] text-xs rounded-full font-medium whitespace-nowrap">
                      {t('New', '最新')}
                    </span>
                  )}
                </div>

                {/* Content */}
                <p className="text-white/90 leading-relaxed text-sm">
                  {lang === 'en' && review.contentEn ? review.contentEn : review.content}
                </p>

                {/* Footer: name + course + date */}
                <div className="pt-2 border-t border-white/8 flex items-end justify-between gap-2">
                  <div>
                    <p className="text-white text-sm font-semibold leading-none mb-1">{review.name}</p>
                    <span className="text-[#d4af37] text-xs">
                      {lang === 'en' && review.courseEn ? review.courseEn : review.course}
                    </span>
                  </div>
                  <span className="text-white/30 text-xs whitespace-nowrap flex-shrink-0">{review.date}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Show more / less */}
          {wallReviews.length > 9 && (
            <div className="text-center mt-10">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="inline-flex items-center gap-2 px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] rounded-full text-sm hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className={showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                {showAll ? t('Show Less', '收起') : t(`Show All ${wallReviews.length} Reviews`, `查看全部 ${wallReviews.length} 条反馈`)}
              </button>
            </div>
          )}

          {/* ── Feedback Form ── */}
          <div className="mt-20 pt-16 border-t border-white/8">
            <div className="text-center mb-10">
              <p className="text-[#d4af37] text-sm font-semibold tracking-widest uppercase mb-3">{t('Share Your Experience', '分享你的体验')}</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">{t('Leave a Review', '写下你的反馈')}</h3>
              <p className="text-[#c8b6ff]">{t('Your feedback will appear on the wall above instantly.', '提交后你的反馈会立即出现在上方反馈墙中。')}</p>
            </div>

            {!currentUser ? (
              <div className="max-w-lg mx-auto bg-[#2d1b4e]/60 backdrop-blur-md rounded-2xl border border-[#d4af37]/20 p-6 sm:p-10 flex flex-col items-center text-center gap-5">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
                  <i className="ri-lock-line text-[#d4af37] text-2xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-lg mb-1">{t('Login Required', '请先登录')}</p>
                  <p className="text-[#c8b6ff] text-sm leading-relaxed">
                    {t('Only logged-in students can leave a review. Sign in to share your experience.', '只有已登录的学员才能提交反馈，请登录后继续。')}
                  </p>
                </div>
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-8 py-3 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] font-bold rounded-full hover:shadow-lg hover:shadow-[#d4af37]/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t('Sign In / Sign Up', '登录 / 注册')}
                </button>
              </div>
            ) : submitStatus === 'success' ? (
              <div className="max-w-lg mx-auto bg-[#2d1b4e]/60 backdrop-blur-md rounded-2xl border border-green-500/30 p-6 sm:p-10 flex flex-col items-center text-center gap-5">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/30">
                  <i className="ri-checkbox-circle-line text-green-400 text-3xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-lg mb-1">{t('Published to the Wall!', '已发布到反馈墙！')}</p>
                  <p className="text-[#c8b6ff] text-sm">{t('Scroll up to see your review at the top of the wall.', '向上滚动即可看到你的反馈已出现在墙的最前面。')}</p>
                </div>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="px-6 py-2.5 border border-[#d4af37]/40 text-[#d4af37] rounded-full text-sm hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t('Write Another Review', '再写一条反馈')}
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id="student-feedback-form"
                data-readdy-form
                onSubmit={handleFeedbackSubmit}
                className="max-w-2xl mx-auto bg-[#2d1b4e]/60 backdrop-blur-md rounded-2xl border border-[#d4af37]/20 p-5 sm:p-8 space-y-6"
              >
                {/* User info */}
                <div className="flex items-center gap-3 pb-5 border-b border-white/10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d4af37]/15 border border-[#d4af37]/30 flex-shrink-0">
                    <i className="ri-user-line text-[#d4af37] text-base"></i>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{currentUser.name}</p>
                    <p className="text-[#c8b6ff] text-xs">{currentUser.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { localStorage.removeItem('mystic_current_user'); setCurrentUser(null); }}
                    className="ml-auto text-[#c8b6ff]/50 hover:text-[#c8b6ff] text-xs transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('Switch Account', '切换账户')}
                  </button>
                </div>

                {/* Name input */}
                <div>
                  <label className="block text-[#c8b6ff] text-sm font-medium mb-2">{t('Your Name', '你的名字')} <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <i className="ri-user-3-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm pointer-events-none"></i>
                    <input
                      type="text"
                      name="name"
                      value={feedbackForm.name}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                      required
                      maxLength={50}
                      placeholder={t('Enter your name or nickname', '输入你的名字或昵称')}
                      className="w-full pl-9 pr-4 py-3 bg-[#1a0b2e]/60 border border-[#d4af37]/25 rounded-lg text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#d4af37] transition-colors"
                    />
                  </div>
                </div>

                {/* Course select */}
                <div>
                  <label className="block text-[#c8b6ff] text-sm font-medium mb-2">{t('Course Attended', '参加的课程')} <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <i className="ri-book-open-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm pointer-events-none"></i>
                    <select
                      name="course"
                      value={feedbackForm.course}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, course: e.target.value })}
                      required
                      className="w-full pl-9 pr-4 py-3 bg-[#1a0b2e]/60 border border-[#d4af37]/25 rounded-lg text-sm text-white focus:outline-none focus:border-[#d4af37] transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-[#1a0b2e]">{t('Select a course...', '选择课程...')}</option>
                      {courses.flatMap((c) => {
                        const enHasBoth = c.nameEn.includes('Level I & II');
                        const zhHasBoth = c.nameZh.includes('一阶&二阶');
                        if (enHasBoth || zhHasBoth) {
                          return [
                            <option key={`${c.id}-1`} value={`${c.nameEn.replace('Level I & II', 'Level I')}`} className="bg-[#1a0b2e]">
                              {t(c.nameEn.replace('Level I & II', 'Level I'), c.nameZh.replace('一阶&二阶', '一阶'))}
                            </option>,
                            <option key={`${c.id}-2`} value={`${c.nameEn.replace('Level I & II', 'Level II')}`} className="bg-[#1a0b2e]">
                              {t(c.nameEn.replace('Level I & II', 'Level II'), c.nameZh.replace('一阶&二阶', '二阶'))}
                            </option>,
                          ];
                        }
                        return [
                          <option key={c.id} value={c.nameEn} className="bg-[#1a0b2e]">
                            {t(c.nameEn, c.nameZh)}
                          </option>,
                        ];
                      })}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm pointer-events-none"></i>
                  </div>
                </div>

                {/* Star rating */}
                <div>
                  <label className="block text-[#c8b6ff] text-sm font-medium mb-3">{t('Overall Rating', '综合评分')} <span className="text-red-400">*</span></label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setFeedbackForm((f) => ({ ...f, hoverRating: star }))}
                        onMouseLeave={() => setFeedbackForm((f) => ({ ...f, hoverRating: 0 }))}
                        onClick={() => setFeedbackForm((f) => ({ ...f, rating: star }))}
                        className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                      >
                        <i className={`${(feedbackForm.hoverRating || feedbackForm.rating) >= star ? 'ri-star-fill text-[#d4af37]' : 'ri-star-line text-white/30'} text-2xl transition-colors`}></i>
                      </button>
                    ))}
                    {feedbackForm.rating > 0 && (
                      <span className="text-[#d4af37] text-sm ml-1">
                        {['', t('Poor', '很差'), t('Fair', '一般'), t('Good', '不错'), t('Great', '很好'), t('Excellent', '非常好')][feedbackForm.rating]}
                      </span>
                    )}
                  </div>
                </div>

                {/* Feedback content */}
                <div>
                  <label className="block text-[#c8b6ff] text-sm font-medium mb-2">
                    {t('Your Feedback', '你的反馈内容')} <span className="text-red-400">*</span>
                    <span className="ml-2 text-white/30 text-xs font-normal">{feedbackForm.content.length}/500</span>
                  </label>
                  <textarea
                    name="content"
                    value={feedbackForm.content}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setFeedbackForm({ ...feedbackForm, content: e.target.value });
                    }}
                    required
                    rows={4}
                    placeholder={t('Share what you learned, how it changed you, or what stood out most...', '分享你的收获、改变，或者最让你印象深刻的内容...')}
                    className="w-full px-4 py-3 bg-[#1a0b2e]/60 border border-[#d4af37]/25 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-[#d4af37] transition-colors resize-none leading-relaxed"
                  />
                  {feedbackForm.content.length >= 500 && (
                    <p className="text-amber-400 text-xs mt-1">{t('Maximum 500 characters reached.', '已达到500字上限。')}</p>
                  )}
                </div>

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-red-500/15 border border-red-500/30 rounded-lg text-red-300 text-sm">
                    <i className="ri-error-warning-line"></i>
                    <span>{t('Submission failed. Please try again.', '提交失败，请稍后重试。')}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'loading' || !feedbackForm.name.trim() || !feedbackForm.course || !feedbackForm.rating || !feedbackForm.content.trim()}
                  className="w-full py-3.5 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] font-bold rounded-lg hover:shadow-lg hover:shadow-[#d4af37]/30 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitStatus === 'loading' ? (
                    <><i className="ri-loader-4-line animate-spin text-lg"></i> {t('Publishing...', '发布中...')}</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> {t('Publish to Wall', '发布到反馈墙')}</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Community />

      <section className="relative z-10 bg-gradient-to-b from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e]">
        <FAQ />
      </section>

      <Footer />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}
